import * as THREE from 'three';
import { DSTEEL, DWOOD, cloth, emis, metal } from '../materials.ts';
import { M, glowSprite } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import * as cb from './class-buildings.ts';
import * as e2 from './era2.ts';
import * as e4 from './era4.ts';
import { forge } from './forge.ts';
import { branchCrown } from './branches.ts';
import * as land from './land.ts';
import { levelUp } from './levels.ts';
import * as ob from './obras.ts';
import * as s2 from './saberes2.ts';
import * as st from './structures.ts';
import type { Model } from './types.ts';

/*
 * A structure as it stands on the board: its model, clad in iron when its material is 3 (era II), crowned with
 * its branch when a tower took one (docs/OBRAS.md), raised on the pedestal of its level when it is a tower
 * (levels 2 to 6). The engine bakes these; the gallery shows them too.
 */

export type StructLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type StructMaterial = 1 | 2 | 3;

/** Towers stand on a pedestal from level 2 (level 4 needs era II). */
export const TOWER_TYPES = ['ballista', 'catapult', 'arcane', 'belfry', 'beacon', 'clockwork', 'trebuchet'];
/** Era II buildings model every material themselves. */
const ERA2_TYPES = ['workshop', 'clockwork', 'lab'];
/** Class buildings of the barbarian and the druid: like the era II ones, they model every material and ruin themselves. */
const CLASS_TYPES = ['grove', 'hall'];
/** Era IV: the watchtower, the sanctum and the trebuchet model every material and ruin themselves too. */
const ERA4_TYPES = ['watchtower', 'sanctum', 'trebuchet'];
/** Terraced land: the gathering post and the golem works model every material and ruin themselves. */
const LAND_TYPES = ['depot', 'golemworks'];
/** The eras' pieces on terraced land (obras.ts): foundry, arsenal, lightning rod, gate tower, Titan's Eye; the market (a saber). */
const OBRA_TYPES: string[] = ob.OBRA_TYPES;
/** The saberes' structures (saberes2.ts): the boulder, the kennel, the crane, the sieve. */
const SABER_TYPES: string[] = s2.SABER_TYPES;
/** Types whose own builder draws material 3 (iron); the rest are clad by `ironclad`. */
const NATIVE_IRON = ['ballista', 'wall', 'gate', 'forge', ...ERA2_TYPES, ...CLASS_TYPES, ...ERA4_TYPES, ...LAND_TYPES, ...OBRA_TYPES, ...SABER_TYPES];

const make = (t: string, lv: StructLevel, material: StructMaterial, wear: st.Wear): Model => {
  // A level 5 or 6 tower is its level 4 self on the taller Vigil or titan pedestal (`raise`).
  const level = (lv > 4 ? 4 : lv) as 1 | 2 | 3 | 4;
  switch (t) {
    case 'ballista':
      return st.ballista({ level, material, wear });
    case 'catapult':
      return st.catapult({ level, wear });
    case 'belfry':
      return st.belfry({ level, wear });
    case 'arcane':
      return st.arcane({ level, wear });
    case 'beacon':
      return st.beacon({ level, wear });
    case 'wall':
      return st.wall({ material, wear });
    case 'gate':
      return st.gate({ material, wear });
    case 'spikes':
      return st.spikes({});
    case 'oil':
      return st.oil({});
    case 'forge':
      return forge({ level: lv, material, wear });
    case 'barracks':
      return st.barracks({ wear });
    case 'temple':
      return st.temple({ wear });
    case 'workshop':
      return e2.workshop({ material, wear });
    case 'clockwork':
      return e2.clockworkTower({ level, material, wear });
    case 'lab':
      return e2.lab({ material, wear });
    case 'grove':
      return cb.grove({ material, wear });
    case 'hall':
      return cb.clanHall({ material, wear });
    case 'watchtower':
      return e4.watchtower({ material, wear });
    case 'sanctum':
      return e4.sanctum({ material, wear });
    case 'trebuchet':
      return e4.trebuchet({ level, material, wear });
    case 'depot':
      return land.depot({ material, wear });
    case 'golemworks':
      return land.golemWorks({ level: (level > 3 ? 3 : level) as 1 | 2 | 3, material, wear });
    case 'foundry':
      return ob.foundry({ material, wear });
    case 'arsenal':
      return ob.arsenal({ material, wear });
    case 'rod':
      return ob.rod({ material, wear });
    case 'gatetower':
      // It grows with its level itself (it stands at a gate, on no pedestal).
      return ob.gateTower({ level: lv, material, wear });
    case 'titanEye':
      return ob.titanEye({ material, wear });
    case 'market':
      return ob.market({ material, wear });
    case 'kennel':
      return s2.kennel({ level: lv, material, wear });
    case 'crane':
      return s2.crane({ material, wear });
    case 'sieve':
      return s2.sieve({ material, wear });
    default:
      return st.wall({ material: 1 });
  }
};

/**
 * Builds `type` at `level` in `material` (1 wood, 2 stone, 3 iron), worn to stage `wear`; a tower that took a
 * `branch` (content BRANCHES) wears its crown.
 */
export const structure = (type: string, level: StructLevel, material: StructMaterial, wear: st.Wear = 0, rampartLength?: number, branch?: string): Model => {
  // The ring draws the wall itself; this run is only for cards and sheets.
  if (type === 'rampart') return st.curtain({ length: rampartLength ?? 1.2 });
  // The boulder is a trap with one charge: `spent` (in the branch's place of the key) is its empty chock.
  if (type === 'boulder') return s2.boulder({ level, material, spent: branch === 'spent' });
  // Buildings to 3 and traps to 5 show their level with an added module (levels.ts); level 1 is unchanged.
  const m = levelUp(make(type, level, material, wear), type, level, material, wear);
  if (material === 3 && !NATIVE_IRON.includes(type)) ironclad(m, type, wear);
  if (branch) branchCrown(m, type, branch, wear, Math.min(4, level));
  // Towers reach level 6 in era V, on the titan stone.
  if (TOWER_TYPES.includes(type) && level > 1) return raise(m, Math.min(6, level) as 2 | 3 | 4 | 5 | 6, type === 'catapult' || type === 'trebuchet', wear);
  return m;
};

/** What a destroyed structure leaves, in its material (iron ruins: slate stone and torn plates in the rubble). */
export const ruinModel = (type: string, material: number): Model => {
  const mat = (material === 3 ? 3 : material === 1 ? 1 : 2) as StructMaterial;
  if (ERA2_TYPES.includes(type)) return e2.era2Ruin(type, mat);
  if (CLASS_TYPES.includes(type)) return cb.classRuin(type, mat);
  if (ERA4_TYPES.includes(type)) return e4.era4Ruin(type, mat);
  if (LAND_TYPES.includes(type)) return land.landRuin(type, mat);
  if (OBRA_TYPES.includes(type)) return ob.obraRuin(type, mat);
  if (SABER_TYPES.includes(type)) return s2.saberRuin(type, mat);
  const m = st.ruin({ type, material: mat === 1 ? 1 : 2 });
  if (mat === 3) {
    recolorIron(m);
    const g = (m.root.getObjectByName('model') as THREE.Group | undefined) ?? m.root;
    const rng = mulberry32(970 + type.length * 7);
    for (let i = 0; i < 6; i++) {
      const a = rng() * Math.PI * 2;
      const d = 0.25 + rng() * 0.35;
      const p = M(new THREE.BoxGeometry(0.13 + rng() * 0.08, 0.012, 0.09 + rng() * 0.06), st.IRON_PLATE, g);
      p.position.set(Math.sin(a) * d, 0.02 + rng() * 0.04, Math.cos(a) * d);
      p.rotation.set((rng() - 0.5) * 0.7, rng() * 3, (rng() - 0.5) * 0.4);
    }
    const band = M(new THREE.TorusGeometry(0.24, 0.014, 4, 14, Math.PI * 0.85), st.IRON_BAND, g);
    band.position.set(0.25, 0.03, -0.3);
    band.rotation.set(-Math.PI / 2 + 0.2, 0, 1.1);
  }
  return m;
};

/* ---------- iron cladding (material 3) ---------- */

const IRON_ROOF = metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true });
const ROOFS = new Set(['3e3a36', '4a2f22', '3a271a']);
const SLATE = new THREE.Color(st.IRON_STONE);
const IRONED = new Map<string, THREE.Material>();

/** Stone under iron goes a cold slate; slate, shingle and plank roofs become iron sheet. Timber and lit parts stay. */
const recolorIron = (m: Model): void => {
  m.root.traverse((o) => {
    if (o.userData.part === 'crew') return;
    let p: THREE.Object3D | null = o.parent;
    while (p) {
      if (p.userData.part === 'crew') return;
      p = p.parent;
    }
    const mesh = o as THREE.Mesh;
    const src = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mesh.isMesh || !src?.isMeshStandardMaterial || src.transparent || (src.emissiveIntensity > 0.2 && src.emissive.getHex() !== 0)) return;
    const hex = src.color.getHexString();
    if (ROOFS.has(hex)) {
      mesh.material = IRON_ROOF;
      return;
    }
    if (!src.flatShading || src.metalness > 0.3) return;
    let next = IRONED.get(src.uuid);
    if (!next) {
      const c = src.clone();
      c.color.lerp(SLATE, 0.5);
      next = c;
      IRONED.set(src.uuid, c);
    }
    mesh.material = next;
  });
};

/**
 * Clads a stone or timber structure in iron: riveted plates over its lower courses, bands and angle irons
 * higher up, iron roofs. Each type gets its plating fitted to its own body.
 */
export const ironclad = (m: Model, type: string, wear: st.Wear): Model => {
  recolorIron(m);
  const g = (m.root.getObjectByName('model') as THREE.Group | undefined) ?? m.root;
  const ring = (x: number, y: number, z: number, r: number): void => {
    const b = M(new THREE.CylinderGeometry(r, r, 0.035, 8, 1, true), st.IRON_BAND, g);
    b.position.set(x, y, z);
  };
  switch (type) {
    case 'catapult': {
      // Iron-plated rails and brackets, iron discs on the wheels, the arm strapped.
      for (const side of [1, -1]) for (const x of [-0.3, 0, 0.3]) st.plate(g, x, 0.22, side * 0.285, 0.27, 0.085, side > 0 ? 0 : Math.PI);
      for (const x of [-0.32, 0.32]) for (const z of [-0.3, 0.3]) {
        if (wear === 2 && x > 0 && z > 0) continue;
        const d = M(new THREE.CylinderGeometry(0.095, 0.095, 0.012, 10), st.IRON_PLATE, g);
        d.rotation.x = Math.PI / 2;
        d.position.set(x, 0.13, z + Math.sign(z) * 0.036);
        M(new THREE.OctahedronGeometry(0.018, 0), st.IRON_RIVET, g).position.set(x, 0.13, z + Math.sign(z) * 0.044);
      }
      for (const x of [-0.45, 0.45]) for (const z of [-0.24, 0.24]) M(new THREE.BoxGeometry(0.03, 0.12, 0.1), st.IRON_BAND, g).position.set(x, 0.22, z);
      const arm = m.root.getObjectByName('arm');
      if (arm) for (const x of [-0.15, 0.15, 0.42]) M(new THREE.BoxGeometry(0.025, 0.06, 0.06), st.IRON_BAND, arm).position.set(x, 0, 0);
      break;
    }
    case 'arcane':
      st.roundClad(g, 0.45, 0.32, 0, 1.0, 8, 0, 0.38, [0.52, 0.88]);
      break;
    case 'belfry': {
      e2.boxClad(g, 0, 0, 0.6, 0.6, 0, 1.3, [0.12, 0.66, 1.24], 0.42, ['l', 'b', 'r', 'f']);
      for (const x of [-0.24, 0.24]) for (const z of [-0.24, 0.24]) {
        M(new THREE.BoxGeometry(0.12, 0.035, 0.12), st.IRON_BAND, g).position.set(x, 1.33, z);
        if (!(wear === 2 && x > 0 && z > 0)) M(new THREE.BoxGeometry(0.12, 0.035, 0.12), st.IRON_BAND, g).position.set(x, 1.8, z);
      }
      break;
    }
    case 'beacon':
      st.roundClad(g, 0.42, 0.3, 0, 1.7, 10, 0, 0.5, [0.9, 1.45]);
      break;
    case 'barracks': {
      e2.boxClad(g, 0, 0, 0.92, 0.56, 0.1, 0.52, [0.15, 0.47], 0, []);
      const door = M(new THREE.BoxGeometry(0.17, 0.31, 0.012), st.IRON_PLATE, g);
      door.position.set(0, 0.25, 0.3);
      for (const y of [0.15, 0.35]) M(new THREE.BoxGeometry(0.18, 0.025, 0.01), st.IRON_BAND, g).position.set(0, y, 0.307);
      break;
    }
    case 'temple': {
      // Iron rings up the columns, an iron trim round the lintel and the step.
      for (const x of [-0.34, 0.34]) for (const z of [-0.34, 0.34]) {
        const broken = wear === 2 && x > 0 && z > 0;
        for (const y of broken ? [0.26, 0.5] : [0.26, 0.52, 0.8]) ring(x, y, z, 0.072);
      }
      for (const [w, d, y] of [[0.88, 0.88, 0.95], [0.88, 0.88, 0.12]] as const) {
        if (y > 0.5 && wear === 2) continue;
        for (const s of [1, -1]) {
          M(new THREE.BoxGeometry(w, 0.035, 0.012), st.IRON_BAND, g).position.set(0, y, (s * d) / 2);
          M(new THREE.BoxGeometry(0.012, 0.035, d), st.IRON_BAND, g).position.set((s * w) / 2, y, 0);
        }
      }
      break;
    }
  }
  return m;
};

/* ---------- tower pedestals (levels 2 to 4) ---------- */

const LEVEL_MATS = {
  stone: new THREE.MeshStandardMaterial({ color: '#8f8a82', roughness: 0.95, flatShading: true }),
  stoneDark: new THREE.MeshStandardMaterial({ color: '#6f6a63', roughness: 0.95, flatShading: true }),
  deck: new THREE.MeshStandardMaterial({ color: '#6b4a2c', roughness: 0.9 }),
  steel: DSTEEL,
  pole: DWOOD,
  pennant: new THREE.MeshStandardMaterial({ color: '#b3262a', roughness: 0.8, side: THREE.DoubleSide }),
  rune: emis('#ffc0a8', '#ff4f3a', 1.3),
  vigil: emis('#f2f6ff', '#bcd0ff', 1.4),
  pale: new THREE.MeshStandardMaterial({ color: '#b3aea6', roughness: 0.95, flatShading: true }),
  silver: metal('#c9d0da', { roughness: 0.3 }),
  crimson: cloth('#7a1d22'),
  emblem: cloth('#d9dce3'),
  // Era V: the titan stone, dark slate cut with pale runes (the Vigil's light).
  titan: new THREE.MeshStandardMaterial({ color: '#3b3f47', roughness: 0.95, flatShading: true }),
  titanDark: new THREE.MeshStandardMaterial({ color: '#2a2d33', roughness: 0.95, flatShading: true }),
};

/** Height of the titan-stone foot a level 6 tower stands on, under its level 5 pedestal. */
const TITAN_FOOT = 0.16;

/**
 * Upgraded towers stand on a pedestal: level 2 a dressed-stone base (a timber deck for the catapult),
 * level 3 a taller one with a steel band and red pennants at the corners, level 4 a two-tier bastion:
 * a broad dark plinth, a drum girdled in iron with dragon-red runes burning in its faces, buttresses
 * crowned with spikes and crimson hangings on four sides (the catapult a double timber stage, iron-shod).
 */
export const raise = (m: Model, lv: 2 | 3 | 4 | 5 | 6, timber: boolean, wear: st.Wear = 0): Model => {
  // Level 5 (era IV, the Vigil's bastion) is level 4's bastion grown taller and broader, its runes burning the
  // Vigil's silver-white instead of dragon red, a silver band round the drum. Level 6 (era V) is that bastion
  // built in titan stone, dark slate under its pale runes, set on a broad titan-stone foot cut with more of them.
  const titan = lv === 6;
  const vigil = lv >= 5;
  const level = vigil ? 4 : (lv as 2 | 3 | 4);
  const H = vigil ? 0.64 : level === 4 ? 0.52 : level === 3 ? 0.36 : 0.22;
  const R = vigil ? 0.78 : level === 4 ? 0.74 : level === 3 ? 0.7 : 0.64;
  const body = new THREE.Group();
  body.position.y = H;
  for (const child of [...m.root.children]) body.add(child);
  m.root.add(body);
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.rotation.y = ry;
    m.root.add(mesh);
    return mesh;
  };
  const pennants = (r: number, y0: number, h: number): void => {
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + (i * Math.PI) / 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      add(new THREE.CylinderGeometry(0.018, 0.022, h, 5), LEVEL_MATS.pole, x, y0 + h / 2, z);
      const flag = add(new THREE.PlaneGeometry(0.26, 0.15), LEVEL_MATS.pennant, x, y0 + h * 0.88, z, -a);
      flag.position.x += Math.cos(a + Math.PI / 2) * 0.13;
      flag.position.z += Math.sin(a + Math.PI / 2) * 0.13;
    }
  };
  if (level === 4) {
    if (timber) {
      // Two timber stages, the upper one stepped in, iron at every corner.
      add(new THREE.BoxGeometry(R * 2 + 0.06, 0.24, R * 2 + 0.06), LEVEL_MATS.deck, 0, 0.12, 0);
      add(new THREE.BoxGeometry(R * 2 - 0.08, 0.28, R * 2 - 0.08), LEVEL_MATS.deck, 0, 0.24 + 0.14, 0);
      for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        add(new THREE.BoxGeometry(0.12, H + 0.06, 0.12), LEVEL_MATS.pole, x * (R - 0.02), (H + 0.06) / 2, z * (R - 0.02));
        for (const y of [0.08, 0.3, H - 0.02]) add(new THREE.BoxGeometry(0.135, 0.04, 0.135), DSTEEL, x * (R - 0.02), y, z * (R - 0.02));
        add(new THREE.ConeGeometry(0.05, 0.12, 4), DSTEEL, x * (R - 0.02), H + 0.12, z * (R - 0.02), Math.PI / 4);
      }
      for (const s of [1, -1]) {
        add(new THREE.BoxGeometry(R * 2 + 0.07, 0.04, 0.02), DSTEEL, 0, 0.24, s * (R + 0.035));
        add(new THREE.BoxGeometry(0.02, 0.04, R * 2 + 0.07), DSTEEL, s * (R + 0.035), 0.24, 0);
      }
      const hang = add(new THREE.BoxGeometry(0.3, 0.36, 0.014), LEVEL_MATS.crimson, 0, 0.26, R + 0.045);
      hang.position.y = 0.3;
      add(new THREE.BoxGeometry(0.08, 0.08, 0.018), LEVEL_MATS.emblem, 0, 0.33, R + 0.05, 0).rotation.z = Math.PI / 4;
      st.pedestalWear(m.root, R, H, true, wear);
      pennants(R - 0.02, H, 1.0);
    } else {
      const rot = Math.PI / 8;
      const k = Math.cos(Math.PI / 8);
      add(new THREE.CylinderGeometry(R + 0.06, R + 0.12, 0.16, 8), LEVEL_MATS.stoneDark, 0, 0.08, 0, rot);
      add(new THREE.CylinderGeometry(R + 0.14, R + 0.16, 0.04, 8), LEVEL_MATS.stoneDark, 0, 0.02, 0, rot);
      const r1 = R - 0.04, r0 = R;
      // The drum stops just under the dark cap: two tops at the same height would flicker against each other.
      add(new THREE.CylinderGeometry(r1, r0, H - 0.17, 8), titan ? LEVEL_MATS.titan : vigil ? LEVEL_MATS.pale : LEVEL_MATS.stone, 0, 0.16 + (H - 0.17) / 2, 0, rot);
      add(new THREE.CylinderGeometry(r1 + 0.04, r1 + 0.02, 0.06, 8), LEVEL_MATS.stoneDark, 0, H - 0.03, 0, rot);
      for (const y of [0.19, H - 0.09]) add(new THREE.CylinderGeometry(r0 + 0.012 - (y - 0.16) * 0.1, r0 + 0.012 - (y - 0.16) * 0.1, 0.045, 8, 1, true), LEVEL_MATS.steel, 0, y, 0, rot);
      // Faces at angles i·π/4: runes on the cardinal faces, buttresses on the diagonals.
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        const ap = (r0 - 0.02) * k;
        if (i % 2 === 0) {
          const rune = add(new THREE.BoxGeometry(0.035, 0.13, 0.012), vigil ? LEVEL_MATS.vigil : LEVEL_MATS.rune, Math.sin(a) * (ap + 0.004), 0.33, Math.cos(a) * (ap + 0.004), a);
          rune.rotation.y = a;
          for (const dy of [-0.045, 0.045]) add(new THREE.BoxGeometry(0.08, 0.012, 0.012), vigil ? LEVEL_MATS.vigil : LEVEL_MATS.rune, Math.sin(a) * (ap + 0.004), 0.33 + dy, Math.cos(a) * (ap + 0.004), a);
          const glow = new THREE.Vector3(Math.sin(a) * (ap + 0.06), 0.33, Math.cos(a) * (ap + 0.06));
          glowSprite(m.root, vigil ? '#dfe8ff' : '#ff6a4a', 0.22, glow, 0.55);
          // Crimson hangings beside each rune, on an iron rod.
          for (const s of [-1, 1]) {
            const t = a + s * 0.24;
            const hang = add(new THREE.BoxGeometry(0.11, 0.26, 0.012), LEVEL_MATS.crimson, Math.sin(t) * (ap + 0.012), 0.31, Math.cos(t) * (ap + 0.012), t);
            hang.rotation.y = t;
          }
          add(new THREE.BoxGeometry(0.4, 0.022, 0.025), DSTEEL, Math.sin(a) * (ap + 0.02), 0.45, Math.cos(a) * (ap + 0.02), a);
        } else {
          const bx = Math.sin(a) * (ap + 0.04), bz = Math.cos(a) * (ap + 0.04);
          add(new THREE.BoxGeometry(0.15, H + 0.04, 0.16), titan ? LEVEL_MATS.titanDark : LEVEL_MATS.stone, bx, (H + 0.04) / 2, bz, a);
          add(new THREE.BoxGeometry(0.17, 0.05, 0.18), LEVEL_MATS.stoneDark, bx, H + 0.065, bz, a);
          if (vigil) {
            // The Vigil's flame on every buttress: a silver-white tongue and its glow.
            add(new THREE.ConeGeometry(0.05, 0.2, 5), LEVEL_MATS.vigil, bx, H + 0.19, bz, a);
            glowSprite(m.root, '#dfe8ff', 0.42, new THREE.Vector3(bx, H + 0.22, bz), 0.75);
          } else add(new THREE.ConeGeometry(0.045, 0.16, 4), DSTEEL, bx, H + 0.17, bz, a + Math.PI / 4);
          add(new THREE.BoxGeometry(0.16, 0.035, 0.17), LEVEL_MATS.steel, bx, 0.3, bz, a);
        }
      }
      if (vigil) add(new THREE.CylinderGeometry(r0 + 0.02, r0 + 0.02, 0.04, 8, 1, true), LEVEL_MATS.silver, 0, 0.58, 0, rot);
      st.pedestalWear(m.root, r0 - 0.02, H, false, wear);
      // Taller poles from the buttress tops.
      pennants((r0 - 0.02) * k + 0.04, H + 0.09, 1.05);
    }
    m.height = Math.round((m.height + H) * 100) / 100;
    if (titan) titanFoot(m, timber ? R + 0.1 : R + 0.16, wear);
    return m;
  }
  if (timber) {
    add(new THREE.BoxGeometry(R * 2, H, R * 2), LEVEL_MATS.deck, 0, H / 2, 0);
    for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) add(new THREE.BoxGeometry(0.1, H + 0.04, 0.1), LEVEL_MATS.pole, x * (R - 0.04), H / 2, z * (R - 0.04));
  } else {
    add(new THREE.CylinderGeometry(R - 0.04, R, H, 8), LEVEL_MATS.stone, 0, H / 2, 0, Math.PI / 8);
    add(new THREE.CylinderGeometry(R + 0.02, R + 0.04, 0.06, 8), LEVEL_MATS.stoneDark, 0, 0.03, 0, Math.PI / 8);
  }
  st.pedestalWear(m.root, timber ? R : R - 0.02, H, timber, wear);
  if (level === 3) {
    add(new THREE.CylinderGeometry(R + 0.01, R + 0.01, 0.05, 8, 1, true), LEVEL_MATS.steel, 0, H - 0.06, 0, Math.PI / 8);
    pennants(R - 0.05, H, 0.95);
  }
  m.height = Math.round((m.height + H) * 100) / 100;
  return m;
};

/**
 * The titan-stone foot under a level 6 tower: everything the tower was is lifted onto a broad octagonal slab of
 * dark slate, pale runes cut in its faces (the Vigil's light, geometry only) and four of them glowing.
 */
const titanFoot = (m: Model, R: number, wear: st.Wear): void => {
  const lift = new THREE.Group();
  lift.position.y = TITAN_FOOT;
  for (const child of [...m.root.children]) lift.add(child);
  m.root.add(lift);
  const rot = Math.PI / 8;
  const k = Math.cos(Math.PI / 8);
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.rotation.y = ry;
    m.root.add(mesh);
    return mesh;
  };
  add(new THREE.CylinderGeometry(R, R + 0.06, TITAN_FOOT - 0.03, 8), LEVEL_MATS.titan, 0, (TITAN_FOOT - 0.03) / 2, 0, rot);
  add(new THREE.CylinderGeometry(R + 0.02, R, 0.03, 8), LEVEL_MATS.titanDark, 0, TITAN_FOOT - 0.015, 0, rot);
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const ap = (R + 0.03) * k + 0.004;
    const x = Math.sin(a) * ap, z = Math.cos(a) * ap;
    // A rune: a stroke and a chevron over it.
    add(new THREE.BoxGeometry(0.022, 0.075, 0.01), LEVEL_MATS.vigil, x, TITAN_FOOT / 2 - 0.01, z, a);
    for (const s of [-1, 1]) add(new THREE.BoxGeometry(0.05, 0.012, 0.01), LEVEL_MATS.vigil, x, TITAN_FOOT / 2 + 0.025, z, a).rotation.set(0, a, s * 0.6);
    if (i % 2 === 0) glowSprite(m.root, '#dfe8ff', 0.26, new THREE.Vector3(Math.sin(a) * (ap + 0.05), TITAN_FOOT / 2, Math.cos(a) * (ap + 0.05)), wear === 2 ? 0.35 : 0.55);
  }
  if (wear > 0) {
    const rng = mulberry32(960 + wear);
    for (let i = 0; i < wear + 1; i++) st.crackRound(m.root, (R + 0.03) * k, TITAN_FOOT * 0.6, 0.6 + i * 1.9 + rng() * 0.3, 0.1, rng);
  }
  m.height = Math.round((m.height + TITAN_FOOT) * 100) / 100;
};
